import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import webHookAPI from '../../api/webhooks';

const state = {
  records: [],
  operationsByWebhookId: {},
  operationUIFlagsByWebhookId: {},
  uiFlags: {
    fetchingList: false,
    creatingItem: false,
    deletingItem: false,
    updatingItem: false,
  },
};

export const getters = {
  getWebhooks(_state) {
    return _state.records.sort((w1, w2) => w1.id - w2.id);
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getWebhookOperations: _state => id => {
    return _state.operationsByWebhookId[id] || null;
  },
  getWebhookOperationsUIFlags: _state => id => {
    return {
      isFetchingOperations: false,
      hasOperationsError: false,
      isRunningPreflight: false,
      hasPreflightError: false,
      isReconciling: false,
      reconcilingDeliveryId: null,
      isReplaying: false,
      replayingDeliveryId: null,
      ...(_state.operationUIFlagsByWebhookId[id] || {}),
    };
  },
};

export const actions = {
  async get({ commit }) {
    commit(types.default.SET_WEBHOOK_UI_FLAG, { fetchingList: true });
    try {
      const response = await webHookAPI.get();
      commit(types.default.SET_WEBHOOK, response.data.payload.webhooks);
      commit(types.default.SET_WEBHOOK_UI_FLAG, { fetchingList: false });
    } catch (error) {
      commit(types.default.SET_WEBHOOK_UI_FLAG, { fetchingList: false });
    }
  },

  async create({ commit }, params) {
    commit(types.default.SET_WEBHOOK_UI_FLAG, { creatingItem: true });
    try {
      const response = await webHookAPI.create(params);
      const {
        payload: { webhook },
      } = response.data;
      commit(types.default.ADD_WEBHOOK, webhook);
      commit(types.default.SET_WEBHOOK_UI_FLAG, { creatingItem: false });
      return webhook;
    } catch (error) {
      commit(types.default.SET_WEBHOOK_UI_FLAG, { creatingItem: false });
      throw error;
    }
  },

  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.default.SET_WEBHOOK_UI_FLAG, { updatingItem: true });
    try {
      const response = await webHookAPI.update(id, updateObj);
      commit(types.default.UPDATE_WEBHOOK, response.data.payload.webhook);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.default.SET_WEBHOOK_UI_FLAG, { updatingItem: false });
    }
  },

  async delete({ commit }, id) {
    commit(types.default.SET_WEBHOOK_UI_FLAG, { deletingItem: true });
    try {
      await webHookAPI.delete(id);
      commit(types.default.DELETE_WEBHOOK, id);
      commit(types.default.SET_WEBHOOK_UI_FLAG, { deletingItem: false });
    } catch (error) {
      commit(types.default.SET_WEBHOOK_UI_FLAG, { deletingItem: false });
      throw error;
    }
  },

  async fetchOperationsSummary({ commit }, { id, limit = 10 }) {
    commit(types.default.SET_WEBHOOK_OPERATIONS_UI_FLAG, {
      id,
      uiFlags: { isFetchingOperations: true, hasOperationsError: false },
    });
    try {
      const response = await webHookAPI.operationsSummary(id, { limit });
      const operations = response.data.payload.webhook_operations;
      commit(types.default.SET_WEBHOOK_OPERATIONS, { id, operations });
      return operations;
    } catch (error) {
      commit(types.default.SET_WEBHOOK_OPERATIONS_UI_FLAG, {
        id,
        uiFlags: { hasOperationsError: true },
      });
      throw error;
    } finally {
      commit(types.default.SET_WEBHOOK_OPERATIONS_UI_FLAG, {
        id,
        uiFlags: { isFetchingOperations: false },
      });
    }
  },

  async runPreflight({ commit, state: currentState }, { id }) {
    commit(types.default.SET_WEBHOOK_OPERATIONS_UI_FLAG, {
      id,
      uiFlags: { isRunningPreflight: true, hasPreflightError: false },
    });
    try {
      const response = await webHookAPI.preflight(id);
      const preflight = response.data.payload.webhook_preflight;
      commit(types.default.SET_WEBHOOK_OPERATIONS, {
        id,
        operations: {
          ...(currentState.operationsByWebhookId[id] || {}),
          endpoint_preflight: preflight,
        },
      });
      return preflight;
    } catch (error) {
      commit(types.default.SET_WEBHOOK_OPERATIONS_UI_FLAG, {
        id,
        uiFlags: { hasPreflightError: true },
      });
      throw error;
    } finally {
      commit(types.default.SET_WEBHOOK_OPERATIONS_UI_FLAG, {
        id,
        uiFlags: { isRunningPreflight: false },
      });
    }
  },

  async reconcileUnknown(
    { commit },
    {
      id,
      deliveryId,
      state: reconciliationState,
      providerEventId,
      providerMessageId,
      failureClassification,
      errorCode,
      reason,
      reconciliationKey,
      evidence,
    }
  ) {
    commit(types.default.SET_WEBHOOK_RECONCILIATION_UI_FLAG, {
      id,
      uiFlags: { isReconciling: true, reconcilingDeliveryId: deliveryId },
    });
    try {
      const response = await webHookAPI.reconcileUnknown(id, deliveryId, {
        state: reconciliationState,
        ...(providerEventId ? { provider_event_id: providerEventId } : {}),
        ...(providerMessageId
          ? { provider_message_id: providerMessageId }
          : {}),
        ...(failureClassification
          ? { failure_classification: failureClassification }
          : {}),
        ...(errorCode ? { error_code: errorCode } : {}),
        ...(reason ? { reason } : {}),
        ...(reconciliationKey ? { reconciliation_key: reconciliationKey } : {}),
        ...(evidence ? { evidence } : {}),
      });
      return response.data.payload.webhook_reconciliation;
    } finally {
      commit(types.default.SET_WEBHOOK_RECONCILIATION_UI_FLAG, {
        id,
        uiFlags: { isReconciling: false, reconcilingDeliveryId: null },
      });
    }
  },

  async replayDelivery({ commit }, { id, deliveryId, reason, replayKey }) {
    commit(types.default.SET_WEBHOOK_REPLAY_UI_FLAG, {
      id,
      uiFlags: { isReplaying: true, replayingDeliveryId: deliveryId },
    });
    try {
      const response = await webHookAPI.replayDelivery(id, deliveryId, {
        reason,
        ...(replayKey ? { replay_key: replayKey } : {}),
      });
      return response.data.payload.webhook_replay;
    } finally {
      commit(types.default.SET_WEBHOOK_REPLAY_UI_FLAG, {
        id,
        uiFlags: { isReplaying: false, replayingDeliveryId: null },
      });
    }
  },
};

export const mutations = {
  [types.default.SET_WEBHOOK_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_WEBHOOK_OPERATIONS](_state, { id, operations }) {
    _state.operationsByWebhookId = {
      ..._state.operationsByWebhookId,
      [id]: operations,
    };
  },
  [types.default.SET_WEBHOOK_OPERATIONS_UI_FLAG](_state, { id, uiFlags }) {
    _state.operationUIFlagsByWebhookId = {
      ..._state.operationUIFlagsByWebhookId,
      [id]: {
        ...(_state.operationUIFlagsByWebhookId[id] || {}),
        ...uiFlags,
      },
    };
  },
  [types.default.SET_WEBHOOK_RECONCILIATION_UI_FLAG](_state, { id, uiFlags }) {
    _state.operationUIFlagsByWebhookId = {
      ..._state.operationUIFlagsByWebhookId,
      [id]: {
        ...(_state.operationUIFlagsByWebhookId[id] || {}),
        ...uiFlags,
      },
    };
  },
  [types.default.SET_WEBHOOK_REPLAY_UI_FLAG](_state, { id, uiFlags }) {
    _state.operationUIFlagsByWebhookId = {
      ..._state.operationUIFlagsByWebhookId,
      [id]: {
        ...(_state.operationUIFlagsByWebhookId[id] || {}),
        ...uiFlags,
      },
    };
  },
  [types.default.SET_WEBHOOK]: MutationHelpers.set,
  [types.default.ADD_WEBHOOK]: MutationHelpers.create,
  [types.default.DELETE_WEBHOOK]: MutationHelpers.destroy,
  [types.default.UPDATE_WEBHOOK]: MutationHelpers.update,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
